import { useState } from 'react';
import { Show } from '@refinedev/antd';
import { useCan, useDelete, useGo, useShow } from '@refinedev/core';
import {
  Descriptions,
  Tag,
  Button,
  Space,
  Popconfirm,
  Input,
  Alert,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { BidiText, MonoCode, formatDateBidi } from '@lebinatikra/rtl-utils';
import { FilterApiError } from '../../lib/filter-api-client';

interface CommunityRow {
  id: string;
  name: string;
  slug: string;
  profile: string;
  data_region: string;
  created_at: string;
  rav_kehilla_user_id: string | null;
}

export function CommunityShow() {
  const { t, i18n } = useTranslation();
  const { query } = useShow<CommunityRow>();
  const record = query.data?.data;

  const can = useCan({ resource: 'communities', action: 'delete' });
  const canDelete = can.data?.can === true;

  const canEdit = useCan({ resource: 'communities', action: 'edit' });
  const canEditPolicy = canEdit.data?.can === true;

  const { mutate: deleteMutate, mutation } = useDelete();
  const isDeleting = mutation.isPending;
  const go = useGo();
  const [reason, setReason] = useState('');

  const onConfirm = () => {
    if (!record) return;
    deleteMutate(
      {
        resource: 'communities',
        id: record.id,
        // Reason is optional; only send when non-empty so backend Zod-strict
        // doesn't choke on empty-string vs absent.
        values: reason.trim() ? { reason: reason.trim() } : undefined,
        // Soft-delete may 409 on dependencies — no optimistic UI.
        mutationMode: 'pessimistic',
        successNotification: () => ({
          message: t('notifications.community_deleted'),
          type: 'success',
        }),
        errorNotification: (err) => {
          const httpErr = err as { errors?: { code?: string } } | undefined;
          if (httpErr?.errors?.code === 'HAS_ACTIVE_DEPENDENCIES') {
            return { message: t('errors.has_active_dependencies'), type: 'error' };
          }
          if (err instanceof FilterApiError) {
            return {
              message: i18n.language === 'en' ? err.messageEn : err.message,
              type: 'error',
            };
          }
          return { message: t('errors.generic'), type: 'error' };
        },
      },
      {
        onSuccess: () => {
          go({ to: { resource: 'communities', action: 'list' } });
        },
        onError: () => {
          // toasts handled via errorNotification above; no rethrow needed.
        },
      },
    );
  };

  return (
    <Show isLoading={query.isLoading}>
      {record ? (
        <>
          <Descriptions column={1} bordered>
            <Descriptions.Item label={t('common.id')}>
              {/* rtl-utils:bidi-wrapped */}
              <MonoCode>{record.id}</MonoCode>
            </Descriptions.Item>
            <Descriptions.Item label={t('common.name')}>
              {/* rtl-utils:bidi-wrapped */}
              <BidiText>{record.name}</BidiText>
            </Descriptions.Item>
            <Descriptions.Item label={t('fields.slug')}>
              {/* rtl-utils:bidi-wrapped */}
              <MonoCode>{record.slug}</MonoCode>
            </Descriptions.Item>
            <Descriptions.Item label={t('fields.profile')}>
              <Tag>{record.profile}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('fields.data_region')}>
              <Tag color="geekblue">{record.data_region}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('common.created_at')}>
              {formatDateBidi(record.created_at)}
            </Descriptions.Item>
            {record.rav_kehilla_user_id ? (
              <Descriptions.Item label={t('fields.rav_kehilla_user_id')}>
                {/* rtl-utils:bidi-wrapped */}
                <MonoCode>{record.rav_kehilla_user_id}</MonoCode>
              </Descriptions.Item>
            ) : null}
          </Descriptions>
          <Space style={{ marginTop: 16 }}>
            <Link to={`/communities/${record.id}/organizations`}>
              <Button type="primary">{t('actions.open_organizations')}</Button>
            </Link>
            {canEditPolicy ? (
              <Link to={`/communities/${record.id}/policy`}>
                <Button data-testid="open-policy-editor">{t('actions.open_policy_editor')}</Button>
              </Link>
            ) : null}
            {canDelete ? (
              <Popconfirm
                title={t('actions.delete_community')}
                description={
                  <Space direction="vertical" style={{ width: 320 }}>
                    <Alert
                      type="warning"
                      showIcon
                      message={t('confirm.soft_delete_warning')}
                    />
                    <Input.TextArea
                      data-testid="soft-delete-reason"
                      placeholder={t('confirm.soft_delete_reason_label')}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={2}
                      maxLength={500}
                    />
                  </Space>
                }
                onConfirm={onConfirm}
                okButtonProps={{ loading: isDeleting, danger: true }}
                okText={t('common.delete')}
                cancelText={t('common.cancel')}
              >
                <Button danger data-testid="delete-community-btn">
                  {t('actions.delete_community')}
                </Button>
              </Popconfirm>
            ) : null}
          </Space>
        </>
      ) : null}
    </Show>
  );
}
